import React, { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import './PagamentoColaborador.css'

// Constantes de roles
const USER_ROLES = {
  ADMINISTRADOR: 'administrador',
  INSTALADOR: 'instalador'
}

const PagamentoColaborador = ({ userRole, userEmpresa }) => {
  const [pagamentos, setPagamentos] = useState([])
  const [resumoPagamentos, setResumoPagamentos] = useState([])
  const [resumoDiario, setResumoDiario] = useState([])
  const [colaboradoresPeriodo, setColaboradoresPeriodo] = useState([])
  const [dataInicio, setDataInicio] = useState('')
  const [dataFim, setDataFim] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [viewMode, setViewMode] = useState('colaboradores') // 'colaboradores', 'detalhado', 'resumo' ou 'diario'

  // Definir datas padrão (mês atual)
  useEffect(() => {
    const hoje = new Date()
    const primeiroDia = new Date(hoje.getFullYear(), hoje.getMonth(), 1)
    const ultimoDia = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0)
    
    setDataInicio(primeiroDia.toISOString().split('T')[0])
    setDataFim(ultimoDia.toISOString().split('T')[0])
  }, [])

  const carregarPagamentos = async () => {
    if (!dataInicio || !dataFim) {
      setError('Por favor, selecione as datas de início e fim')
      return
    }

    setLoading(true)
    setError('')

    try {
      console.log('🔍 Carregando pagamentos...', { userRole, userEmpresa: userEmpresa?.id, dataInicio, dataFim })

      // Se for administrador, carregar todos os pagamentos
      if (userRole === 'administrador') {
        console.log('👑 Usuário é administrador - carregando todos os pagamentos')
        
        // Consulta detalhada - buscar presenças
        const { data: presencas, error: errorPresencas } = await supabase
          .from('presenca')
          .select(`
            id,
            data_presenca,
            presenca_colaboradores!inner(
              presente,
              colaborador_id,
              colaboradores!inner(
                id,
                nome,
                cargo,
                valor_pagamento,
                ativo,
                empresa_id,
                tipos_pagamentos!inner(
                  tipo_pagamento
                )
              )
            )
          `)
          .gte('data_presenca', `${dataInicio}T00:00:00`)
          .lte('data_presenca', `${dataFim}T23:59:59`)
          .eq('presenca_colaboradores.presente', true)
          .eq('presenca_colaboradores.colaboradores.ativo', true)

        if (errorPresencas) throw errorPresencas

        // Buscar adiantamentos/descontos do período
        const { data: adiantamentos, error: errorAdiantamentos } = await supabase
          .from('adiantamentos_colaboradores')
          .select(`
            colaborador_id,
            valor_pago,
            pgto_tipos!inner(
              descricao
            ),
            colaboradores!inner(
              nome,
              empresa_id
            )
          `)
          .gte('data_lancamento', dataInicio)
          .lte('data_lancamento', dataFim)

        if (errorAdiantamentos) throw errorAdiantamentos

        console.log('✅ Pagamentos carregados (admin):', presencas?.length || 0, 'presenças,', adiantamentos?.length || 0, 'adiantamentos')
        
        // Processar dados (mesmo código de processamento)
        processarDados(presencas, adiantamentos)
        return
      }

      // Para usuários não-administradores, filtrar por empresa
      if (!userEmpresa?.id) {
        console.log('❌ Usuário não tem empresa definida')
        setError('Usuário não tem empresa definida')
        return
      }

      console.log('🏢 Filtrando pagamentos por empresa:', userEmpresa.id)

      // Buscar colaboradores da empresa
      const { data: colaboradoresEmpresa, error: errorColaboradores } = await supabase
        .from('colaboradores')
        .select('id')
        .eq('empresa_id', userEmpresa.id)
        .eq('ativo', true)

      if (errorColaboradores) {
        console.error('❌ Erro ao carregar colaboradores da empresa:', errorColaboradores)
        throw errorColaboradores
      }

      const colaboradoresIds = colaboradoresEmpresa?.map(c => c.id) || []
      console.log('👥 Colaboradores da empresa encontrados:', colaboradoresIds.length, colaboradoresIds)

      if (colaboradoresIds.length === 0) {
        console.log('⚠️ Nenhum colaborador encontrado para a empresa')
        setError('Nenhum colaborador encontrado para a empresa')
        return
      }

      // Consulta detalhada - buscar presenças filtradas por empresa
      const { data: presencas, error: errorPresencas } = await supabase
        .from('presenca')
        .select(`
          id,
          data_presenca,
          presenca_colaboradores!inner(
            presente,
            colaborador_id,
            colaboradores!inner(
              id,
              nome,
              cargo,
              valor_pagamento,
              ativo,
              empresa_id,
              tipos_pagamentos!inner(
                tipo_pagamento
              )
            )
          )
        `)
        .gte('data_presenca', `${dataInicio}T00:00:00`)
        .lte('data_presenca', `${dataFim}T23:59:59`)
        .eq('presenca_colaboradores.presente', true)
        .eq('presenca_colaboradores.colaboradores.ativo', true)
        .in('presenca_colaboradores.colaborador_id', colaboradoresIds)

      if (errorPresencas) throw errorPresencas

      // Buscar adiantamentos/descontos do período filtrados por empresa
      const { data: adiantamentos, error: errorAdiantamentos } = await supabase
        .from('adiantamentos_colaboradores')
        .select(`
          colaborador_id,
          valor_pago,
          pgto_tipos!inner(
            descricao
          ),
          colaboradores!inner(
            nome,
            empresa_id
          )
        `)
        .gte('data_lancamento', dataInicio)
        .lte('data_lancamento', dataFim)
        .eq('colaboradores.empresa_id', userEmpresa.id)

      if (errorAdiantamentos) throw errorAdiantamentos

      console.log('✅ Pagamentos carregados (filtrados):', presencas?.length || 0, 'presenças,', adiantamentos?.length || 0, 'adiantamentos')
      
      // Processar dados (mesmo código de processamento)
      processarDados(presencas, adiantamentos)

    } catch (err) {
      console.error('❌ Erro ao carregar pagamentos:', err)
      setError('Erro ao carregar dados de pagamento: ' + err.message)
    } finally {
      setLoading(false)
    }
  }

  // Função para processar os dados (extraída da lógica original)
  const processarDados = (presencas, adiantamentos) => {
    // Processar dados detalhados
    const pagamentosProcessados = presencas?.flatMap(presenca => 
      presenca.presenca_colaboradores?.map(pc => {
        const colaborador = pc.colaboradores
        const tipoPagamento = colaborador.tipos_pagamentos.tipo_pagamento
        const valorBase = colaborador.valor_pagamento
        const dataPresenca = presenca.data_presenca

        // Calcular valor do dia baseado no tipo de pagamento
        let valorDia = 0
        const dataInicioObj = new Date(dataInicio)
        const dataFimObj = new Date(dataFim)
        const diasPeriodo = Math.ceil((dataFimObj - dataInicioObj) / (1000 * 60 * 60 * 24)) + 1
        
        switch (tipoPagamento) {
          case 'Avulso':
          case 'Diaria':
            valorDia = valorBase
            break
          case 'Quinzenal':
            valorDia = valorBase / 15
            break
          case 'Mensal':
            valorDia = valorBase / 30
            break
          default:
            valorDia = 0
        }

        return {
          data_presenca: dataPresenca,
          colaborador_nome: colaborador.nome,
          cargo: colaborador.cargo,
          tipo_pagamento: tipoPagamento,
          valor_pagamento: valorBase,
          valor_dia: valorDia,
          valor_pago: valorDia,
          dias_periodo: diasPeriodo
        }
      }) || []
    ) || []

    // Ordenar por data de presença
    pagamentosProcessados.sort((a, b) => new Date(a.data_presenca) - new Date(b.data_presenca))

    setPagamentos(pagamentosProcessados)

    // Calcular resumo por colaborador
    const resumo = {}
    pagamentosProcessados.forEach(pagamento => {
      const key = pagamento.colaborador_nome
      if (!resumo[key]) {
        resumo[key] = {
          colaborador_nome: pagamento.colaborador_nome,
          cargo: pagamento.cargo,
          tipo_pagamento: pagamento.tipo_pagamento,
          valor_base: pagamento.valor_pagamento,
          dias_presentes: 0,
          total_pagamento: 0,
          total_pagamentos_extras: 0, // Pagamentos do tipo "Pagamento"
          total_adiantamentos: 0,
          total_descontos: 0,
          saldo_final: 0
        }
      }
      resumo[key].dias_presentes += 1
      resumo[key].total_pagamento += pagamento.valor_pago
    })

    // Adicionar adiantamentos/descontos/pagamentos ao resumo
    adiantamentos?.forEach(adiantamento => {
      const colaboradorNome = adiantamento.colaboradores.nome
      const tipoPgto = adiantamento.pgto_tipos.descricao
      const valor = adiantamento.valor_pago

      if (resumo[colaboradorNome]) {
        if (tipoPgto === 'Adiantamento') {
          resumo[colaboradorNome].total_adiantamentos += valor
        } else if (tipoPgto === 'Desconto') {
          resumo[colaboradorNome].total_descontos += valor
        } else if (tipoPgto === 'Pagamento') {
          // Pagamentos extras são contabilizados separadamente
          resumo[colaboradorNome].total_pagamentos_extras += valor
        }
      }
    })

    // Calcular saldo final para cada colaborador
    Object.values(resumo).forEach(colaborador => {
      colaborador.saldo_final = colaborador.total_pagamento - colaborador.total_adiantamentos - colaborador.total_descontos - colaborador.total_pagamentos_extras
    })

    // Ordenar resumo por nome do colaborador
    const resumoOrdenado = Object.values(resumo).sort((a, b) => 
      a.colaborador_nome.localeCompare(b.colaborador_nome)
    )
    setResumoPagamentos(resumoOrdenado)

    // Calcular resumo diário
    const resumoDiario = {}
    pagamentosProcessados.forEach(pagamento => {
      const data = pagamento.data_presenca
      if (!resumoDiario[data]) {
        resumoDiario[data] = {
          data_presenca: data,
          total_colaboradores: 0,
          total_pagamento: 0,
          colaboradores: []
        }
      }
      resumoDiario[data].total_colaboradores += 1
      resumoDiario[data].total_pagamento += pagamento.valor_pago
      resumoDiario[data].colaboradores.push({
        nome: pagamento.colaborador_nome,
        cargo: pagamento.cargo,
        tipo_pagamento: pagamento.tipo_pagamento,
        valor_pago: pagamento.valor_pago
      })
    })

    // Ordenar resumo diário por data
    const resumoDiarioOrdenado = Object.values(resumoDiario).sort((a, b) => 
      new Date(a.data_presenca) - new Date(b.data_presenca)
    )
    setResumoDiario(resumoDiarioOrdenado)

    // Criar estrutura de colaboradores por período com dias recolhidos
    const colaboradoresComDias = {}
    const diasPeriodo = pagamentosProcessados.length > 0 ? pagamentosProcessados[0].dias_periodo : 0
    
    pagamentosProcessados.forEach(pagamento => {
      const key = pagamento.colaborador_nome
      if (!colaboradoresComDias[key]) {
        colaboradoresComDias[key] = {
          colaborador_nome: pagamento.colaborador_nome,
          cargo: pagamento.cargo,
          tipo_pagamento: pagamento.tipo_pagamento,
          valor_base: pagamento.valor_pagamento,
          dias_presentes: 0,
          total_pagamento: 0,
          total_pagamentos_extras: 0, // Pagamentos do tipo "Pagamento"
          total_adiantamentos: 0,
          total_descontos: 0,
          saldo_final: 0,
          dias_periodo: diasPeriodo,
          dias_detalhados: []
        }
      }
      colaboradoresComDias[key].dias_presentes += 1
      colaboradoresComDias[key].total_pagamento += pagamento.valor_pago
      colaboradoresComDias[key].dias_detalhados.push({
        data_presenca: pagamento.data_presenca,
        valor_pago: pagamento.valor_pago
      })
    })

    // Adicionar adiantamentos/descontos/pagamentos aos colaboradores
    adiantamentos?.forEach(adiantamento => {
      const colaboradorNome = adiantamento.colaboradores.nome
      const tipoPgto = adiantamento.pgto_tipos.descricao
      const valor = adiantamento.valor_pago

      if (colaboradoresComDias[colaboradorNome]) {
        if (tipoPgto === 'Adiantamento') {
          colaboradoresComDias[colaboradorNome].total_adiantamentos += valor
        } else if (tipoPgto === 'Desconto') {
          colaboradoresComDias[colaboradorNome].total_descontos += valor
        } else if (tipoPgto === 'Pagamento') {
          // Pagamentos extras são contabilizados separadamente
          colaboradoresComDias[colaboradorNome].total_pagamentos_extras += valor
        }
      }
    })

    // Calcular saldo final para cada colaborador
    Object.values(colaboradoresComDias).forEach(colaborador => {
      colaborador.saldo_final = colaborador.total_pagamento - colaborador.total_adiantamentos - colaborador.total_descontos - colaborador.total_pagamentos_extras
    })

    // Ordenar dias detalhados por data para cada colaborador
    Object.values(colaboradoresComDias).forEach(colaborador => {
      colaborador.dias_detalhados.sort((a, b) => new Date(a.data_presenca) - new Date(b.data_presenca))
    })

    // Ordenar colaboradores por nome
    const colaboradoresOrdenados = Object.values(colaboradoresComDias).sort((a, b) => 
      a.colaborador_nome.localeCompare(b.colaborador_nome)
    )
    setColaboradoresPeriodo(colaboradoresOrdenados)
  }

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor)
  }

  const getSaldoFinalClass = (valor) => {
    const baseClass = 'fw-bold saldo-final-cell'
    return valor < 0 ? `${baseClass} text-danger` : `${baseClass} text-success`
  }

  const formatarData = (data) => {
    try {
      // Se a data já está no formato YYYY-MM-DD, usar diretamente
      if (typeof data === 'string' && data.match(/^\d{4}-\d{2}-\d{2}$/)) {
        const [ano, mes, dia] = data.split('-')
        return `${dia}/${mes}/${ano}`
      }
      
      // Caso contrário, converter normalmente
      const dataObj = new Date(data)
      if (isNaN(dataObj.getTime())) {
        return 'Data Inválida'
      }
      
      return dataObj.toLocaleDateString('pt-BR')
    } catch (error) {
      console.error('Erro ao formatar data:', error, 'Data original:', data)
      return 'Erro na Data'
    }
  }

  const calcularTotalGeral = () => {
    return resumoPagamentos.reduce((total, item) => total + item.saldo_final, 0)
  }

  const calcularTotalAdiantamentos = () => {
    return resumoPagamentos.reduce((total, item) => total + item.total_adiantamentos, 0)
  }

  const calcularTotalDescontos = () => {
    return resumoPagamentos.reduce((total, item) => total + item.total_descontos, 0)
  }

  const calcularTotalBruto = () => {
    return resumoPagamentos.reduce((total, item) => total + item.total_pagamento, 0)
  }

  const exportarParaExcel = () => {
    let dados = []
    let headers = []
    
    if (viewMode === 'colaboradores') {
      dados = colaboradoresPeriodo
      headers = ['Colaborador', 'Cargo', 'Tipo Pagamento', 'Valor Base', 'Dias Presentes', 'Adiantamentos', 'Descontos', 'Total Bruto', 'Pago', 'Saldo Final']
    } else if (viewMode === 'detalhado') {
      dados = pagamentos
      headers = ['Data', 'Colaborador', 'Cargo', 'Tipo Pagamento', 'Valor Base', 'Valor Dia', 'Valor Pago']
    } else if (viewMode === 'diario') {
      dados = resumoDiario
      headers = ['Data', 'Total Colaboradores', 'Total Pagamento']
    } else {
      dados = resumoPagamentos
      headers = ['Colaborador', 'Cargo', 'Tipo Pagamento', 'Valor Base', 'Dias Presentes', 'Adiantamentos', 'Descontos', 'Total Bruto', 'Pago', 'Saldo Final']
    }
    
    if (dados.length === 0) {
      alert('Não há dados para exportar')
      return
    }
    
    const csvContent = [
      headers.join(','),
      ...dados.map(item => {
        if (viewMode === 'colaboradores') {
          return [
            `"${item.colaborador_nome}"`,
            `"${item.cargo}"`,
            `"${item.tipo_pagamento}"`,
            item.valor_base,
            item.dias_presentes,
            item.total_adiantamentos,
            item.total_descontos,
            item.total_pagamento,
            item.total_pagamentos_extras,
            item.saldo_final
          ].join(',')
        } else if (viewMode === 'detalhado') {
          return [
            formatarData(item.data_presenca),
            `"${item.colaborador_nome}"`,
            `"${item.cargo}"`,
            `"${item.tipo_pagamento}"`,
            item.valor_pagamento,
            item.valor_dia,
            item.valor_pago
          ].join(',')
        } else if (viewMode === 'diario') {
          return [
            formatarData(item.data_presenca),
            item.total_colaboradores,
            item.total_pagamento
          ].join(',')
        } else {
          return [
            `"${item.colaborador_nome}"`,
            `"${item.cargo}"`,
            `"${item.tipo_pagamento}"`,
            item.valor_base,
            item.dias_presentes,
            item.total_adiantamentos,
            item.total_descontos,
            item.total_pagamento,
            item.total_pagamentos_extras,
            item.saldo_final
          ].join(',')
        }
      })
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `pagamentos_colaboradores_${dataInicio}_${dataFim}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          {/* Cabeçalho com título */}
          <div className="text-center mb-4">
            <h2 className="display-6 fw-bold page-title mb-3">
              💰 Pagamento Colaboradores
            </h2>
          </div>

          {/* Navegação por abas - Card separado */}
          <div className="card mb-4 shadow-sm navigation-card">
            <div className="card-body p-3">
              <div className="row justify-content-center">
                <div className="col-12">
                  <div className="d-flex justify-content-center flex-wrap gap-2" role="group">
                    <button
                      type="button"
                      className={`btn btn-lg ${viewMode === 'colaboradores' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewMode('colaboradores')}
                    >
                      👥 Colaboradores
                    </button>
                    <button
                      type="button"
                      className={`btn btn-lg ${viewMode === 'detalhado' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewMode('detalhado')}
                    >
                      📋 Detalhado
                    </button>
                    <button
                      type="button"
                      className={`btn btn-lg ${viewMode === 'diario' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewMode('diario')}
                    >
                      📅 Diário
                    </button>
                    <button
                      type="button"
                      className={`btn btn-lg ${viewMode === 'resumo' ? 'btn-primary' : 'btn-outline-primary'}`}
                      onClick={() => setViewMode('resumo')}
                    >
                      📊 Resumo
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filtros */}
          <div className="card mb-4">
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <label htmlFor="dataInicio" className="form-label">Data Início</label>
                  <input
                    type="date"
                    className="form-control"
                    id="dataInicio"
                    value={dataInicio}
                    onChange={(e) => setDataInicio(e.target.value)}
                  />
                </div>
                <div className="col-md-4">
                  <label htmlFor="dataFim" className="form-label">Data Fim</label>
                  <input
                    type="date"
                    className="form-control"
                    id="dataFim"
                    value={dataFim}
                    onChange={(e) => setDataFim(e.target.value)}
                  />
                </div>
                <div className="col-md-4 d-flex align-items-end">
                  <button
                    className="btn btn-primary me-2"
                    onClick={carregarPagamentos}
                    disabled={loading}
                  >
                    {loading ? '⏳ Carregando...' : '🔍 Buscar'}
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={exportarParaExcel}
                    disabled={loading || (
                      viewMode === 'colaboradores' ? colaboradoresPeriodo.length === 0 :
                      viewMode === 'detalhado' ? pagamentos.length === 0 :
                      viewMode === 'diario' ? resumoDiario.length === 0 :
                      resumoPagamentos.length === 0
                    )}
                  >
                    📊 Exportar
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Mensagem de erro */}
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          {/* Resumo geral */}
          {resumoPagamentos.length > 0 && (
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">📊 Resumo Geral</h5>
                <div className="row">
                  <div className="col-md-2">
                    <div className="text-center">
                      <h4 className="text-primary">{resumoPagamentos.length}</h4>
                      <small className="text-muted">Colaboradores</small>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="text-center">
                      <h4 className="text-success">{new Set(pagamentos.map(p => p.data_presenca)).size}</h4>
                      <small className="text-muted">Dias com Presença</small>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="text-center">
                      <h4 className="text-info">{formatarMoeda(calcularTotalBruto())}</h4>
                      <small className="text-muted">Total Bruto</small>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="text-center">
                      <h4 className="text-warning">{formatarMoeda(calcularTotalAdiantamentos())}</h4>
                      <small className="text-muted">Adiantamentos</small>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="text-center">
                      <h4 className="text-danger">{formatarMoeda(calcularTotalDescontos())}</h4>
                      <small className="text-muted">Descontos</small>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="text-center">
                      <h4 className="text-success">{formatarMoeda(calcularTotalGeral())}</h4>
                      <small className="text-muted">Saldo Final</small>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Tabela de dados */}
          <div className="card">
            <div className="card-body">
              {loading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Carregando...</span>
                  </div>
                  <p className="mt-2">Carregando dados de pagamento...</p>
                </div>
              ) : viewMode === 'colaboradores' ? (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead className="table-dark">
                      <tr>
                        <th>Colaborador</th>
                        <th>Cargo</th>
                        <th>Tipo Pagamento</th>
                        <th>Valor Base</th>
                        <th>Dias Presentes</th>
                        <th>Adiantamentos</th>
                        <th>Descontos</th>
                        <th>Total Bruto</th>
                        <th>Pago</th>
                        <th>Saldo Final</th>
                        <th>Detalhes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {colaboradoresPeriodo.map((colaborador, index) => (
                        <React.Fragment key={index}>
                          <tr>
                            <td className="fw-bold">{colaborador.colaborador_nome}</td>
                            <td>{colaborador.cargo}</td>
                            <td>
                              <span className={`badge ${
                                colaborador.tipo_pagamento === 'Avulso' ? 'bg-warning' :
                                colaborador.tipo_pagamento === 'Diaria' ? 'bg-info' :
                                colaborador.tipo_pagamento === 'Quinzenal' ? 'bg-success' :
                                'bg-primary'
                              }`}>
                                {colaborador.tipo_pagamento}
                              </span>
                            </td>
                            <td>{formatarMoeda(colaborador.valor_base)}</td>
                            <td>
                              <span className="badge bg-info">{colaborador.dias_presentes}</span>
                            </td>
                            <td className="fw-bold text-warning">{formatarMoeda(colaborador.total_adiantamentos)}</td>
                            <td className="fw-bold text-danger">{formatarMoeda(colaborador.total_descontos)}</td>
                            <td className="fw-bold text-info">{formatarMoeda(colaborador.total_pagamento)}</td>
                            <td className="fw-bold text-success">{formatarMoeda(colaborador.total_pagamentos_extras)}</td>
                            <td className={getSaldoFinalClass(colaborador.saldo_final)}>{formatarMoeda(colaborador.saldo_final)}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target={`#dias-${index}`}
                                aria-expanded="false"
                              >
                                📅 Ver Dias
                              </button>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="10" className="p-0">
                              <div className="collapse" id={`dias-${index}`}>
                                <div className="card card-body">
                                  <h6 className="card-title">Dias trabalhados por {colaborador.colaborador_nome}:</h6>
                                  
                                  {/* Informações do período */}
                                  <div className="mb-3 p-2 bg-info text-white rounded">
                                    <div className="row text-center">
                                      <div className="col-md-4">
                                        <strong>Período: {colaborador.dias_periodo} dias</strong>
                                      </div>
                                      <div className="col-md-4">
                                        <strong>Dias Trabalhados: {colaborador.dias_presentes}</strong>
                                      </div>
                                      <div className="col-md-4">
                                        <strong>Valor por Dia: {formatarMoeda(colaborador.valor_base / (colaborador.tipo_pagamento === 'Mensal' ? 30 : colaborador.tipo_pagamento === 'Quinzenal' ? 15 : 1))}</strong>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Lista de dias - um embaixo do outro */}
                                  <div className="table-responsive">
                                    <table className="table table-sm">
                                      <thead className="table-light">
                                        <tr>
                                          <th>Data</th>
                                          <th>Valor</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {colaborador.dias_detalhados.map((dia, diaIndex) => (
                                          <tr key={diaIndex}>
                                            <td><strong>{formatarData(dia.data_presenca)}</strong></td>
                                            <td className="text-success"><strong>{formatarMoeda(dia.valor_pago)}</strong></td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>

                                  <div className="mt-3 p-2 bg-primary text-white rounded">
                                    <div className="d-flex justify-content-between">
                                      <span><strong>Total do Período:</strong></span>
                                      <span><strong>{formatarMoeda(colaborador.total_pagamento)}</strong></span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  {colaboradoresPeriodo.length === 0 && (
                    <div className="text-center py-4 text-muted">
                      <p>Nenhum colaborador encontrado para o período selecionado.</p>
                    </div>
                  )}
                </div>
              ) : viewMode === 'detalhado' ? (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead className="table-dark">
                      <tr>
                        <th>Data</th>
                        <th>Colaborador</th>
                        <th>Cargo</th>
                        <th>Tipo Pagamento</th>
                        <th>Valor Base</th>
                        <th>Valor Dia</th>
                        <th>Valor Pago</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagamentos.map((pagamento, index) => (
                        <tr key={index}>
                          <td>{formatarData(pagamento.data_presenca)}</td>
                          <td>{pagamento.colaborador_nome}</td>
                          <td>{pagamento.cargo}</td>
                          <td>
                            <span className={`badge ${
                              pagamento.tipo_pagamento === 'Avulso' ? 'bg-warning' :
                              pagamento.tipo_pagamento === 'Diaria' ? 'bg-info' :
                              pagamento.tipo_pagamento === 'Quinzenal' ? 'bg-success' :
                              'bg-primary'
                            }`}>
                              {pagamento.tipo_pagamento}
                            </span>
                          </td>
                          <td>{formatarMoeda(pagamento.valor_pagamento)}</td>
                          <td>{formatarMoeda(pagamento.valor_dia)}</td>
                          <td className="fw-bold text-success">{formatarMoeda(pagamento.valor_pago)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {pagamentos.length === 0 && (
                    <div className="text-center py-4 text-muted">
                      <p>Nenhum registro de pagamento encontrado para o período selecionado.</p>
                    </div>
                  )}
                </div>
              ) : viewMode === 'diario' ? (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead className="table-dark">
                      <tr>
                        <th>Data</th>
                        <th>Total Colaboradores</th>
                        <th>Total Pagamento</th>
                        <th>Detalhes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumoDiario.map((dia, index) => (
                        <React.Fragment key={index}>
                          <tr>
                            <td className="fw-bold">{formatarData(dia.data_presenca)}</td>
                            <td>
                              <span className="badge bg-info">{dia.total_colaboradores}</span>
                            </td>
                            <td className="fw-bold text-success">{formatarMoeda(dia.total_pagamento)}</td>
                            <td>
                              <button
                                className="btn btn-sm btn-outline-primary"
                                type="button"
                                data-bs-toggle="collapse"
                                data-bs-target={`#colaboradores-${index}`}
                                aria-expanded="false"
                              >
                                👥 Ver Colaboradores
                              </button>
                            </td>
                          </tr>
                          <tr>
                            <td colSpan="4" className="p-0">
                              <div className="collapse" id={`colaboradores-${index}`}>
                                <div className="card card-body">
                                  <h6 className="card-title">Colaboradores do dia:</h6>
                                  <div className="row">
                                    {dia.colaboradores.map((colaborador, colIndex) => (
                                      <div key={colIndex} className="col-md-6 mb-2">
                                        <div className="d-flex justify-content-between align-items-center p-2 bg-light rounded">
                                          <div>
                                            <strong>{colaborador.nome}</strong>
                                            <br />
                                            <small className="text-muted">{colaborador.cargo}</small>
                                            <br />
                                            <span className={`badge ${
                                              colaborador.tipo_pagamento === 'Avulso' ? 'bg-warning' :
                                              colaborador.tipo_pagamento === 'Diaria' ? 'bg-info' :
                                              colaborador.tipo_pagamento === 'Quinzenal' ? 'bg-success' :
                                              'bg-primary'
                                            }`}>
                                              {colaborador.tipo_pagamento}
                                            </span>
                                          </div>
                                          <div className="text-end">
                                            <strong className="text-success">{formatarMoeda(colaborador.valor_pago)}</strong>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  {resumoDiario.length === 0 && (
                    <div className="text-center py-4 text-muted">
                      <p>Nenhum resumo diário encontrado para o período selecionado.</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-hover">
                    <thead className="table-dark">
                      <tr>
                        <th>Colaborador</th>
                        <th>Cargo</th>
                        <th>Tipo Pagamento</th>
                        <th>Valor Base</th>
                        <th>Dias Presentes</th>
                        <th>Adiantamentos</th>
                        <th>Descontos</th>
                        <th>Total Bruto</th>
                        <th>Pago</th>
                        <th>Saldo Final</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resumoPagamentos.map((resumo, index) => (
                        <tr key={index}>
                          <td className="fw-bold">{resumo.colaborador_nome}</td>
                          <td>{resumo.cargo}</td>
                          <td>
                            <span className={`badge ${
                              resumo.tipo_pagamento === 'Avulso' ? 'bg-warning' :
                              resumo.tipo_pagamento === 'Diaria' ? 'bg-info' :
                              resumo.tipo_pagamento === 'Quinzenal' ? 'bg-success' :
                              'bg-primary'
                            }`}>
                              {resumo.tipo_pagamento}
                            </span>
                          </td>
                          <td>{formatarMoeda(resumo.valor_base)}</td>
                          <td>
                            <span className="badge bg-info">{resumo.dias_presentes}</span>
                          </td>
                          <td className="fw-bold text-warning">{formatarMoeda(resumo.total_adiantamentos)}</td>
                          <td className="fw-bold text-danger">{formatarMoeda(resumo.total_descontos)}</td>
                          <td className="fw-bold text-info">{formatarMoeda(resumo.total_pagamento)}</td>
                          <td className="fw-bold text-success">{formatarMoeda(resumo.total_pagamentos_extras)}</td>
                          <td className={getSaldoFinalClass(resumo.saldo_final)}>{formatarMoeda(resumo.saldo_final)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {resumoPagamentos.length === 0 && (
                    <div className="text-center py-4 text-muted">
                      <p>Nenhum resumo de pagamento encontrado para o período selecionado.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PagamentoColaborador
